import $ from 'jquery';
import iziToast, { IziToastPosition, IziToastSettings } from 'izitoast';
import 'izitoastcss';

type HttpErrorResponse = {
    status?: number;
    responseText?: string;
    responseJSON?: unknown;
};

const genericProblemTitles = new Set([
    'an error occurred while processing your request.',
    'bad request',
    'forbidden',
    'internal server error',
    'not found',
    'one or more validation errors occurred.',
    'unauthorized',
]);

const requestValidationFailurePattern =
    /\b(?:antiforgery|anti-forgery|request verification token|csrf)\b/i;

const problemMetadataKeys = new Set([
    'detail',
    'instance',
    'message',
    'messages',
    'status',
    'title',
    'traceid',
    'type',
]);

function sanitizeMessage(value: string): string {
    if (!value)
        return '';

    const trimmed = value.trim();
    if (!trimmed
        || genericProblemTitles.has(trimmed.toLowerCase())
        || /<(?:!doctype|html|body|script|style)\b/i.test(trimmed)
        || requestValidationFailurePattern.test(trimmed)
        || /\b(?:System|Microsoft)\.[A-Za-z0-9_.`]+(?:Exception)?\b/.test(trimmed)
        || /\b(?:SqlException|StackTrace|TargetSite|InnerException)\b/i.test(trimmed)
        || /(?:^|\s)at\s+[A-Za-z0-9_.<>`]+\s*\([^)]*\)/m.test(trimmed)
        || /\b[A-Z][A-Z0-9]+(?:_[A-Z0-9]+){1,}\b/.test(trimmed)) {
        return '';
    }

    const sanitized = trimmed
        .replace(/\s*(?:Identificador|Trace\s*Id|TraceId|CorrelationId)\s*:\s*[^\s,;]+/gi, '')
        .replace(/\b0H[A-Z0-9:]+\b/gi, '')
        .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi, '')
        .replace(/\b(?:c[oó]digo\s+(?:do\s+)?erro|erro|http|status(?:\s+code)?)\s*:?\s*[1-5]\d{2}\b/gi, '')
        .replace(/\s*\((?:HTTP\s*)?\d{3}\)\.?/gi, '.')
        .replace(/\s{2,}/g, ' ')
        .trim();

    return sanitized.replace(/^[\s:;,.-]+|[\s:;,.-]+$/g, '').trim();
}

function collectMessages(value: unknown): string[] {
    if (typeof value === 'string') {
        const message = sanitizeMessage(value);
        return message ? [message] : [];
    }
    if (Array.isArray(value))
        return value.flatMap(collectMessages);
    if (value && typeof value === 'object')
        return Object.values(value).flatMap(collectMessages);
    return [];
}

function directValidationMessages(content: object): string[] {
    return Object.entries(content)
        .filter(([key, value]) =>
            !problemMetadataKeys.has(key.toLowerCase())
            && Array.isArray(value))
        .flatMap(([, value]) => collectMessages(value));
}

function problemMessages(content: unknown): string[] {
    if (!content || typeof content !== 'object')
        return collectMessages(content);

    const problem = content as {
        messages?: unknown;
        errors?: unknown;
        detail?: unknown;
        message?: unknown;
        title?: unknown;
    };
    const messages = [
        ...collectMessages(problem.errors),
        ...collectMessages(problem.messages),
        ...collectMessages(problem.detail),
        ...collectMessages(problem.message),
    ];

    // ASP.NET Core can serialize ModelState either inside `errors` or directly
    // as a dictionary whose values are the validation messages.
    if (messages.length === 0)
        messages.push(...directValidationMessages(content));

    if (messages.length === 0
        && !genericProblemTitles.has(String(problem.title || '').trim().toLowerCase()))
        messages.push(...collectMessages(problem.title));

    return [...new Set(messages)];
}

function parseResponseContent(value: unknown): unknown {
    if (typeof value !== 'string')
        return value;

    const text = value.trim();
    if (!text || (text[0] !== '{' && text[0] !== '['))
        return text;

    try {
        return JSON.parse(text);
    } catch {
        return text;
    }
}

function statusMessage(status?: number): string {
    switch (status) {
        case 0:
            return 'Não foi possível conectar ao Thor. Verifique sua conexão e tente novamente.';
        case 400:
            return 'A solicitação não pôde ser validada. Atualize a página e tente novamente. Se o problema continuar, saia e entre novamente no Thor.';
        case 401:
            return 'Sua sessão expirou. Entre novamente no Thor para continuar.';
        case 403:
            return 'Você não possui permissão para realizar esta operação.';
        case 404:
            return 'O conteúdo solicitado não foi encontrado ou não está mais disponível.';
        case 405:
            return 'Esta operação não está disponível nesta tela.';
        case 408:
        case 504:
            return 'O Thor demorou mais que o esperado para responder. Tente novamente.';
        case 409:
            return 'Os dados foram alterados por outra operação. Atualize a página antes de tentar novamente.';
        case 413:
            return 'O arquivo ou conteúdo enviado ultrapassa o tamanho permitido.';
        case 415:
            return 'O formato enviado não é suportado.';
        case 422:
            return 'Revise os dados informados e tente novamente.';
        case 429:
            return 'Foram realizadas muitas tentativas. Aguarde um momento e tente novamente.';
        case 502:
        case 503:
            return 'O serviço está temporariamente indisponível. Tente novamente em alguns instantes.';
        default:
            if (status && status >= 500)
                return 'Não foi possível concluir a operação devido a uma falha interna. Tente novamente. Se o problema continuar, contate o suporte.';
            return 'Não foi possível concluir a operação. Tente novamente.';
    }
}

export function userFacingHttpError(
    status: number | undefined,
    content: unknown,
    fallback?: string): string[] {
    if (status === 401 || status === 403)
        return [statusMessage(status)];

    const messages = problemMessages(parseResponseContent(content));
    if (messages.length > 0)
        return messages;

    const safeFallback = sanitizeMessage(fallback || '');
    const effectiveStatus = status
        ?? (typeof content === 'string' && requestValidationFailurePattern.test(content)
            ? 400
            : undefined);
    return [safeFallback || statusMessage(effectiveStatus)];
}

function escapeHtml(message: string): string {
    return message
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
}

export default class Toast {
    static show(type: string) {
        function showToast(message: string, timeout?: number, position?: IziToastPosition, params?: IziToastSettings) {
            iziToast[type]({
                message,
                timeout: timeout || 10000,
                position: position || 'bottomCenter',
                displayMode: 2,
                ...params
            });
        }

        return function (message: string | HttpErrorResponse, timeout?: number, position?: IziToastPosition, params?: IziToastSettings) {
            if (type === 'error') {
                const response = typeof message === 'string' ? null : message;
                const messages = response
                    ? userFacingHttpError(
                        response.status,
                        response.responseJSON ?? response.responseText)
                    : userFacingHttpError(undefined, message);
                showToast(messages.map(escapeHtml).join('<br>'), timeout, position, params);
                return;
            }

            showToast(String(message), timeout, position, params);
        };
    }

    static httpErrorMessage(status: number, content: unknown, fallback?: string): string {
        return userFacingHttpError(status, content, fallback).join(' ');
    }

    static updateMessage = function (message) {
        $('body > .loading-container').find('.content-message').html(message || '');
    };

    static info = Toast.show('info');
    static success = Toast.show('success');
    static warning = Toast.show('warning');
    static error = Toast.show('error');

    static clear() {
        iziToast.destroy();
    }
}
